/**
 * Pure helpers for the Task Board: due-date wording, the closed window,
 * initials. No React, no I/O — every function takes `now` so tests can pin it.
 */

import { formatCurrency, validPrice } from './formatting';
import { formatPctSigned, gapDirection } from './lotValue';
import { AI_PRICE_STATES } from './aiPrice';

const DAY_MS = 86400000;

export const OPEN_STATUSES = ['open', 'in_progress'];

export const isOpenTask = (task) => OPEN_STATUSES.includes(task?.status);

/**
 * The calendar day of a due date, as a local-midnight Date.
 *
 * A due date is picked as a day and stored as UTC midnight
 * ("2026-10-03T00:00:00Z"); parsing that with new Date() would show the day
 * before anywhere west of UTC. Only the YYYY-MM-DD part carries meaning.
 */
export function toLocalDay(value) {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) return null;
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
}

/**
 * A due date as the YYYY-MM-DD an <input type="date"> wants ('' for none).
 *
 * The API returns due dates with or without a zone ("2026-10-03",
 * "2026-10-03T00:00:00", "2026-10-03T00:00:00Z"); round-tripping any of them
 * through new Date() shifts the day on one side of UTC or the other.
 */
export function toDateInputValue(value) {
    const match = value ? /^\d{4}-\d{2}-\d{2}/.exec(String(value)) : null;
    return match ? match[0] : '';
}

/** The due date to send for a date-input value: the plain day, or null. */
export const toDueDatePayload = (inputValue) => toDateInputValue(inputValue) || null;

/** A due date as "Oct 3, 2026", read as the calendar day it names. */
export function formatDueDate(value) {
    const day = toLocalDay(value);
    return day ? day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Whole calendar days from today to the due date (negative = late). */
export function daysUntilDue(dueDate, now = new Date()) {
    const due = toLocalDay(dueDate);
    if (!due) return null;
    // Math.round absorbs the 23h / 25h days around DST changes.
    return Math.round((due - startOfDay(now)) / DAY_MS);
}

const fmtDay = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const parseTimestamp = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * When a closed task was closed: completed_at for completed tasks. The API
 * keeps no cancel timestamp, so a cancelled task uses updated_at (the status
 * change is normally its last write). Open tasks have none.
 */
export function closedAt(task) {
    if (task?.status === 'completed') return parseTimestamp(task.completed_at || task.updated_at);
    if (task?.status === 'cancelled') return parseTimestamp(task.updated_at);
    return null;
}

export function closedWithinDays(task, days, now = new Date()) {
    const at = closedAt(task);
    if (!at) return false;
    return now.getTime() - at.getTime() <= days * DAY_MS;
}

/**
 * The due pill for a card: { tone, text, late } or null for no pill.
 * tone: 'late' | 'soon' | 'neutral' | 'done' | 'cancelled'.
 */
export function dueInfo(task, now = new Date()) {
    if (task.status === 'completed' || task.status === 'cancelled') {
        const label = task.status === 'completed' ? 'Done' : 'Cancelled';
        const at = closedAt(task);
        return { tone: task.status === 'completed' ? 'done' : 'cancelled', text: at ? `${label} ${fmtDay(at)}` : label, late: false };
    }
    const days = daysUntilDue(task.due_date, now);
    if (days === null) return null;
    if (days < 0) return { tone: 'late', text: `${-days}d late`, late: true };
    if (days === 0) return { tone: 'soon', text: 'Due today', late: false };
    const text = `Due ${fmtDay(toLocalDay(task.due_date))}`;
    return { tone: days <= 7 ? 'soon' : 'neutral', text, late: false };
}

export function isOverdue(task, now = new Date()) {
    if (!isOpenTask(task)) return false;
    const days = daysUntilDue(task.due_date, now);
    return days !== null && days < 0;
}

export function isDueThisWeek(task, now = new Date()) {
    if (!isOpenTask(task)) return false;
    const days = daysUntilDue(task.due_date, now);
    return days !== null && days >= 0 && days <= 7;
}

/**
 * Two-letter initials for an assignee (an email today, a name if one is
 * given): "kyle.hansen@x.com" -> "KH", "andy@x.com" -> "AN".
 */
// "LishanSoosaisanthar" / "kyle.h" / "maria_lopez" -> separate words.
const nameParts = (value) => String(value || '')
    .split('@')[0]
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[\s._-]+/)
    .filter(Boolean);

export function initials(value) {
    if (!value) return '';
    const parts = nameParts(value);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** A readable name from an email when the directory has none: "kyle.h@x" -> "Kyle H". */
export function prettifyPerson(email) {
    return nameParts(email)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

/** Display name for an assignee: the directory name, else the prettified email. */
export function personName(email, directory) {
    if (!email) return '';
    const known = directory?.get(String(email).toLowerCase());
    return known || prettifyPerson(email);
}

/** Number of avatar colours (TaskBoard.css .task-avatar--tone-N). */
export const AVATAR_TONES = 5;

/** A stable colour slot per person, so the same person always looks the same. */
export function avatarTone(key) {
    const text = String(key || '').toLowerCase();
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
    }
    return hash % AVATAR_TONES;
}

// ── Unit context (joined from the inventory already loaded in the app) ──────

/** Inventory units keyed by stock number as a trimmed string. */
export function indexUnitsByStock(units) {
    const map = new Map();
    (units || []).forEach((unit) => {
        if (unit?.StockNumber == null) return;
        const key = String(unit.StockNumber).trim();
        if (key) map.set(key, unit);
    });
    return map;
}

export const unitForTask = (task, unitsByStock) =>
    (unitsByStock && task?.stock_number != null ? unitsByStock.get(String(task.stock_number).trim()) || null : null);

/** Model year as a whole number, or null when the source has none. */
export function unitYear(unit) {
    const year = Number(unit?.YEAR_MANUF);
    return unit?.YEAR_MANUF != null && Number.isFinite(year) && year > 0 ? Math.round(year) : null;
}

/**
 * Make/model in normal case. The DMS stores them upper case ("JOHN DEERE
 * C16F"); plain words of 3+ letters with a vowel are capitalised, codes and
 * vowel-less abbreviations (C16F, VF, MDS, JCB) stay. Mixed-case text is kept.
 */
export function titleCaseModel(text) {
    const value = String(text || '').trim();
    if (!value || /[a-z]/.test(value)) return value;
    return value
        .split(/(\s+)/)
        .map((w) => (/^[A-Z]{3,}$/.test(w) && /[AEIOU]/.test(w) ? w.charAt(0) + w.slice(1).toLowerCase() : w))
        .join('');
}

export const unitDisplayName = (unit) => titleCaseModel([unit?.MAKE, unit?.MODEL].filter(Boolean).join(' '));

/** Thumbnail text: the category code, else the make's initials, else a dash. */
export function thumbLabel(unit) {
    const code = categoryCode(unit?.CATEGORY);
    if (code) return code;
    const make = String(unit?.MAKE || '').trim().split(/\s+/).filter(Boolean);
    if (make.length) return make.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');
    return '\u2014';
}

export function branchCount(units) {
    return new Set((units || []).map((u) => u?.BranchName).filter(Boolean)).size;
}

/** A short code for the thumbnail tile: "CRN" stays, "Tractors" -> "TRA". */
export function categoryCode(category) {
    const value = String(category || '').trim();
    if (!value) return '';
    return (value.length <= 4 ? value : value.slice(0, 3)).toUpperCase();
}

/** "$7k" for thousands and up, whole dollars below. */
export function formatShortMoney(value) {
    const n = Math.abs(value);
    if (n >= 1000) return `$${Math.round(n / 1000).toLocaleString('en-US')}k`;
    return `$${Math.round(n).toLocaleString('en-US')}`;
}

/**
 * The price change a task asks for: { from, to, change, pct, label } or null
 * when either price is missing. change/pct are signed (negative = a cut);
 * label reads "−$7k · 4.9%" / "+$10k · 10.0%".
 */
export function priceCut(task) {
    if (task?.current_price == null || task?.suggested_price == null) return null;
    const from = Number(task.current_price);
    const to = Number(task.suggested_price);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from <= 0) return null;
    const change = to - from;
    const pct = Math.round((change / from) * 1000) / 10;
    const sign = change < 0 ? '−' : '+';
    return { from, to, change, pct, label: `${sign}${formatShortMoney(change)} · ${Math.abs(pct).toFixed(1)}%` };
}

// ── Finding and grouping ──────────────────────────────────────────────────

export const KNOWN_TYPES = ['price_change', 'photo_update', 'description', 'other'];

/** Type chips, mapped to the task.type values the API uses. */
export const TYPE_FILTERS = [
    { id: 'all', label: 'All types' },
    { id: 'price_change', label: 'Price change' },
    { id: 'photo_update', label: 'Photos' },
    { id: 'description', label: 'Description' },
    { id: 'other', label: 'Other' },
];

export function matchesType(task, typeFilter) {
    if (!typeFilter || typeFilter === 'all') return true;
    // A type the board does not know is shown (and filtered) as Other.
    const type = KNOWN_TYPES.includes(task.type) ? task.type : 'other';
    return type === typeFilter;
}

/** Case-insensitive match on stock #, make/model, task title and branch. */
export function matchesSearch(task, unit, query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = [
        task.stock_number,
        task.title,
        unit ? [unit.MAKE, unit.MODEL].filter(Boolean).join(' ') : '',
        unit?.BranchName,
    ].filter((v) => v != null && v !== '').join(' \n ').toLowerCase();
    return haystack.includes(q);
}

export const STATUS_COLUMNS = [
    { key: 'open', label: 'Open', tone: 'open' },
    { key: 'in_progress', label: 'In progress', tone: 'progress' },
    { key: 'completed', label: 'Completed', tone: 'done', closed: true },
    { key: 'cancelled', label: 'Cancelled', tone: 'cancelled', closed: true },
];

export const GROUP_OPTIONS = [
    { id: 'status', label: 'By status' },
    { id: 'assignee', label: 'By person' },
    { id: 'branch', label: 'By branch' },
];

const byLabel = (a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' });

/**
 * Split the (already filtered) tasks into board columns:
 * [{ key, label, tone, tasks, droppable }]. Only status columns accept drops —
 * dropping on a person or branch would have to reassign or move the unit.
 */
export function groupTasks(tasks, groupBy, unitsByStock, { showClosed = false, nameFor = personName } = {}) {
    if (groupBy === 'assignee') {
        const people = Array.from(new Set(tasks.map((t) => t.assigned_to).filter(Boolean)))
            .map((p) => ({ email: p, name: nameFor(p) }))
            .sort((a, b) => byLabel(a.name, b.name));
        return [
            ...people.map((p) => ({ key: `person:${p.email}`, label: p.name, tone: 'person', droppable: false, tasks: tasks.filter((t) => t.assigned_to === p.email) })),
            { key: 'person:none', label: 'Unassigned', tone: 'none', droppable: false, tasks: tasks.filter((t) => !t.assigned_to) },
        ];
    }
    if (groupBy === 'branch') {
        const branchOf = (t) => unitForTask(t, unitsByStock)?.BranchName || null;
        const branches = Array.from(new Set(tasks.map(branchOf).filter(Boolean))).sort(byLabel);
        const cols = branches.map((b) => ({ key: `branch:${b}`, label: b, tone: 'branch', droppable: false, tasks: tasks.filter((t) => branchOf(t) === b) }));
        const noBranch = tasks.filter((t) => !branchOf(t));
        if (noBranch.length) cols.push({ key: 'branch:none', label: 'No branch', tone: 'none', droppable: false, tasks: noBranch });
        return cols;
    }
    return STATUS_COLUMNS
        .filter((c) => !c.closed || showClosed)
        .map((c) => ({ key: c.key, label: c.label, tone: c.tone, droppable: true, tasks: tasks.filter((t) => t.status === c.key) }));
}

/** True when a key press belongs to a text field, not to a board shortcut. */
export function isTypingTarget(el) {
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    const editable = el.getAttribute?.('contenteditable');
    return editable != null && editable !== 'false';
}

// ── The board's view rules (shared by v1 and v2 through hooks/useTaskBoard) ──

/** Closed work older than this stays off the board even with "Show closed" on. */
export const CLOSED_WINDOW_DAYS = 30;

/** Card type labels (the type chips use TYPE_FILTERS' shorter words). */
export const TYPE_LABELS = Object.freeze({
    price_change: 'Price change',
    photo_update: 'Photo update',
    description: 'Description',
    other: 'Other',
});

/** A type the board does not know is shown, counted and filtered as Other. */
export const typeKey = (type) => (TYPE_LABELS[type] ? type : 'other');

/** Dot colour per type chip (v1 class suffixes). */
export const TYPE_DOT_TONE = Object.freeze({ price_change: 'blue', photo_update: 'amber', description: 'teal', other: 'grey' });

/** The three quick-view tiles; each counts all tasks and toggles a filter. */
export const QUICK_VIEWS = Object.freeze([
    { id: 'open', label: 'Open tasks', test: (task) => isOpenTask(task) },
    { id: 'overdue', label: 'Overdue', tone: 'late', test: (task, now) => isOverdue(task, now) },
    { id: 'week', label: 'Due this week', tone: 'soon', test: (task, now) => isDueThisWeek(task, now) },
]);

export const CLOSE_MESSAGES = Object.freeze({ completed: 'Task completed', cancelled: 'Task cancelled' });

/**
 * The Undo toast text for a status change, or null for none: only completing
 * or cancelling offers Undo, and only when the status really changed.
 */
export const undoToastText = (prevStatus, newStatus) => (
    CLOSE_MESSAGES[newStatus] && prevStatus && prevStatus !== newStatus ? CLOSE_MESSAGES[newStatus] : null
);

/** The screen-reader keyboard hint (P-TASK-010). */
export const kbdHintText = (canEdit) => (canEdit
    ? 'Use the arrow keys to move between cards. Press S to start and C to complete the focused task, or Enter to open its unit.'
    : 'Use the arrow keys to move between cards. Press Enter to open the unit.');

/** "<dealer> · N branch(es)"; the count is left out while it is 0. */
export const boardSubtitle = (dealership, branches) => (
    [dealership, branches ? `${branches} ${branches === 1 ? 'branch' : 'branches'}` : ''].filter(Boolean).join(' · ')
);

/** The text of a column without cards. */
export const columnEmptyText = ({ filtersActive, dragEnabled, droppable }) => {
    if (filtersActive) return 'No matching tasks';
    return dragEnabled && droppable ? 'Drop a card here' : 'No tasks';
};

/** Search, type, quick view or assignee — not Show closed, not grouping. */
export const hasActiveFilters = ({ search, typeFilter, quickView, assignee }) => Boolean(
    String(search || '').trim() || (typeFilter && typeFilter !== 'all') || quickView || assignee,
);

export const isRecentlyClosed = (task, now = new Date()) => (
    !isOpenTask(task) && closedWithinDays(task, CLOSED_WINDOW_DAYS, now)
);

/** Does a task pass the board's filters? `skipType` leaves the type filter out. */
export function taskPassesFilters(task, {
    unitsByStock, quickView, assignee, typeFilter, search,
}, now = new Date(), { skipType = false } = {}) {
    const quick = QUICK_VIEWS.find((q) => q.id === quickView);
    if (quick && !quick.test(task, now)) return false;
    if (assignee && task.assigned_to !== assignee) return false;
    if (!skipType && !matchesType(task, typeFilter)) return false;
    return matchesSearch(task, unitForTask(task, unitsByStock), search);
}

const onBoard = (task, showClosed, now) => isOpenTask(task) || (showClosed && isRecentlyClosed(task, now));

/**
 * The tasks on the board under the current filters, and how many recently
 * closed ones "Show closed" would add (0 while it is on).
 */
export function boardTasks(activities, view, now = new Date()) {
    const list = activities || [];
    const filtered = list.filter((a) => onBoard(a, view.showClosed, now) && taskPassesFilters(a, view, now));
    const hiddenClosed = view.showClosed ? 0 : list.filter((a) => isRecentlyClosed(a, now) && taskPassesFilters(a, view, now)).length;
    return { filtered, hiddenClosed };
}

// ── New in the redesign (v2): derived from the tasks already loaded ──────

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Sub-lines under the quick-view tiles (N-03), over all tasks like the counts. */
export function quickViewSummaries(activities, now = new Date()) {
    const list = activities || [];
    const open = list.filter(isOpenTask);
    const inProgress = open.filter((a) => a.status === 'in_progress').length;
    const notStarted = open.length - inProgress;
    const late = list.filter((a) => isOverdue(a, now));
    const oldest = late.reduce((m, a) => Math.max(m, -daysUntilDue(a.due_date, now)), 0);
    const week = list.filter((a) => isDueThisWeek(a, now));
    const soonest = week.reduce((best, a) => {
        const d = daysUntilDue(a.due_date, now);
        return best === null || d < best.days ? { days: d, task: a } : best;
    }, null);
    return {
        open: {
            count: open.length,
            notStarted,
            inProgress,
            text: open.length ? `${notStarted} not started · ${inProgress} in progress` : 'Nothing open',
        },
        overdue: {
            count: late.length,
            oldestDaysLate: oldest,
            text: late.length ? `oldest ${plural(oldest, 'day')} late` : 'None late',
        },
        week: {
            count: week.length,
            text: soonest ? `soonest ${fmtDay(toLocalDay(soonest.task.due_date))} · next 7 days` : 'Nothing due in 7 days',
        },
    };
}

/** Counts on the type chips (N-04): every other filter applies, the type filter does not. */
export function typeChipCounts(activities, view, now = new Date()) {
    const base = (activities || []).filter((a) => onBoard(a, view.showClosed, now) && taskPassesFilters(a, view, now, { skipType: true }));
    const counts = { all: base.length };
    KNOWN_TYPES.forEach((type) => { counts[type] = 0; });
    base.forEach((a) => { counts[typeKey(a.type)] += 1; });
    return counts;
}

/** One pill per active filter (T4): [{ key, label, value }]. */
export function filterPills({ quickView, search, typeFilter, assignee }, nameFor = (e) => e) {
    const pills = [];
    const quick = QUICK_VIEWS.find((q) => q.id === quickView);
    if (quick) pills.push({ key: 'quickView', label: 'View', value: quick.label });
    const q = String(search || '').trim();
    if (q) pills.push({ key: 'search', label: 'Search', value: `"${q}"` });
    if (typeFilter && typeFilter !== 'all') {
        pills.push({ key: 'typeFilter', label: 'Type', value: TYPE_FILTERS.find((f) => f.id === typeFilter)?.label || typeFilter });
    }
    if (assignee) pills.push({ key: 'assignee', label: 'Assignee', value: nameFor(assignee) });
    return pills;
}

/**
 * The new price of a price-change task against the unit's AI value (T1), on
 * the inventory's shared scale (utils/lotValue gapDirection, judged on the
 * displayed one-decimal value). A Review unit shows "Review", as the AI price
 * chip does. Null when there is nothing to compare.
 */
export function suggestedVsAi(task, unit, thresholds) {
    if (task?.type !== 'price_change' || !unit) return null;
    const ai = validPrice(unit.PredictedPrice);
    const price = validPrice(Number(task.suggested_price));
    if (ai === null || price === null || task.suggested_price == null) return null;
    const gap = price / ai - 1;
    const review = unit.PriceStatus === 'Review';
    const state = review ? 'review' : gapDirection(gap, thresholds);
    const pct = formatPctSigned(gap);
    return {
        state,
        gap,
        text: review ? 'Review' : `${pct} vs AI`,
        label: `New price ${formatCurrency(price)} is ${pct} vs the AI value ${formatCurrency(ai)}, ${AI_PRICE_STATES[state]}`,
    };
}
